import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import uploadFile from "@styles/all-images/icons/FileArrowUp.svg";
import {
  formatFileSize,
  handleFileChange,
  handleFileDrop,
  handleCancelUpload,
} from "@/lib/utils/file-utils";
import CustomLoader from "../loader/SpinnerLoader";
import { DocumentApprovalStatus } from "@/constants/enums/document-approval-status.enum";
import ElipsedText from "../elipsis/ElipsedText";

interface FileUploadProps {
  label: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
  error?: string;
  accept?: AcceptedFileTypes[];
  maxFileSize?: number;
  id: string;
  uploadProgress?: number;
  isNA?: boolean;
  disableIsNA?: boolean;
  onNAChange?: (type: "user" | "default", isNA: boolean) => void;
  selectedDocument?: SelectedDocument;
  setUploadProgress?: (uploadProgress: number) => void;
  setCancelUpload?: (cancelUpload: boolean) => void;
  isUploading?: boolean;
  setIsUploading?: (isUploading: boolean) => void;
}

export interface SelectedDocument {
  id?: number;
  file_name?: string;
  status?: DocumentApprovalStatus;
  created_at?: string;
  document_status?: string;
  s3_key?: string;
  size?: number;
  mark_as_na?: boolean;
  updated_at?: string;
  note?: string;
  folder_id?: number;
  version_id?: string;
}

const MAX_FILE_SIZE = 25 * 1000 * 1000; // 25MB in bytes

export enum AcceptedFileTypes {
  PDF = ".pdf",
  DOC = ".doc",
  DOCX = ".docx",
  XLS = ".xls",
  XLSX = ".xlsx",
  CSV = ".csv",
  PNG = ".png",
  JPG = ".jpg",
  JPEG = ".jpeg",
  SVG = ".svg",
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  file,
  onFileChange,
  error,
  accept = [
    AcceptedFileTypes.PDF,
    AcceptedFileTypes.DOC,
    AcceptedFileTypes.DOCX,
    AcceptedFileTypes.XLS,
    AcceptedFileTypes.XLSX,
    AcceptedFileTypes.CSV,
    AcceptedFileTypes.PNG,
    AcceptedFileTypes.JPG,
    AcceptedFileTypes.JPEG,
    AcceptedFileTypes.SVG,
  ],
  maxFileSize = MAX_FILE_SIZE,
  id,
  uploadProgress = 0,
  isNA = false,
  onNAChange,
  disableIsNA,
  selectedDocument,
  setUploadProgress,
  setCancelUpload,
  isUploading,
  setIsUploading,
}) => {
  useEffect(() => {
    if (selectedDocument?.id) {
      setUploadProgress?.(100);
    }
    if (selectedDocument?.mark_as_na) {
      onNAChange?.("default", true);
    }
  }, [selectedDocument]);

  const validateFile = (file: File): boolean => {
    // Check file size
    if (file.size > maxFileSize) {
      toast.error(`File size exceeds the limit. Please upload a smaller file.`);
      return false;
    }

    // Check file type
    const acceptedTypes = accept.map((type) => type.trim().toLowerCase());
    const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;

    if (!acceptedTypes.includes(fileExtension)) {
      const formattedTypes = acceptedTypes
        .map((ext) => {
          return ext.trim().replace(".", "").toUpperCase();
        })
        .join(", ");

      toast.error(
        `Invalid file format. Please upload a ${formattedTypes} document.`,
      );
      return false;
    }

    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (validateFile(selectedFile)) {
        setUploadProgress?.(0);
        onFileChange(selectedFile);
      } else {
        e.target.value = ""; // Reset input
        onFileChange(null);
      }
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove("tw-border-[#146C94]");

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (validateFile(droppedFile)) {
        onFileChange(droppedFile);
      } else {
        onFileChange(null);
      }
    }
  };

  return (
    <Box className="tw-bg-[#F8FAFC] tw-rounded-2xl tw-p-4 tw-border tw-border-[#E5E7EB]">
      <Typography className="tw-text-sm tw-font-medium tw-mb-4 font-primary-color">
        {label}
      </Typography>

      {onNAChange && !disableIsNA && !isUploading && (
        <div className="tw-mb-4">
          <label className="tw-flex tw-items-center tw-gap-2">
            <input
              type="checkbox"
              checked={isNA}
              onChange={(e) => onNAChange("user", e.target.checked)}
              className="tw-h-5 tw-w-5 tw-rounded tw-border-[#D1D5DB] tw-text-[#146C94]"
            />
            <span className="font-gray-color-dark font-size-sm tw-font-medium">
              Mark as N/A
            </span>
          </label>
        </div>
      )}

      {!isNA && (
        <>
          {!isUploading ? (
            <div
              className="tw-border tw-border-dashed tw-border-[#D1D5DB] tw-rounded-lg tw-p-8 tw-mb-4 tw-text-center hover:tw-border-[#146C94] tw-transition-colors"
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add("tw-border-[#146C94]");
              }}
              onDragLeave={(e) => {
                e.currentTarget.classList.remove("tw-border-[#146C94]");
              }}
              onDrop={handleFileDrop}
            >
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
                <div className="tw-w-16 tw-h-16 tw-bg-[#EEF2FF] tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <img
                    src={uploadFile}
                    alt="upload-file"
                    className="tw-w-6 tw-h-6"
                  />
                </div>
                <div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      document.getElementById(id)?.click();
                    }}
                    className="tw-text-[#146C94] tw-font-medium font-size-sm"
                  >
                    Click here
                  </button>
                  <span className="font-size-sm tw-font-medium tw-text-[#809199]">
                    {" "}
                    to upload your file or drag.
                  </span>
                  <div className="tw-mt-2 tw-text-xs tw-text-gray-500">
                    Accepted file types: {accept.join(", ")}
                    <br />
                    Maximum file size: {formatFileSize(maxFileSize)}
                  </div>
                  <input
                    id={id}
                    type="file"
                    className="tw-hidden"
                    onChange={handleFileChange}
                    accept={accept.join(",")}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="tw-flex tw-justify-center tw-items-center tw-h-[230px]">
              <CustomLoader loading={true} isWrapper={true} size="50" />
            </div>
          )}

          {(file ||
            (selectedDocument?.id && !selectedDocument?.mark_as_na)) && (
            <div className="tw-bg-white tw-rounded-lg tw-p-2 tw-border tw-border-[#E5E7EB]">
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                <span className="tw-text-[#1A1A1A] tw-text-sm">
                  <ElipsedText
                    text={file?.name || selectedDocument?.file_name || ""}
                    maxLength={34}
                  />
                </span>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <button
                    onClick={() => {
                      setCancelUpload?.(true);
                    }}
                    className="tw-text-[#6B7280] hover:font-gray-color-dark font-size-sm tw-font-medium"
                  >
                    <svg
                      className="tw-w-5 tw-h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <div className="tw-flex tw-justify-between tw-items-center tw-text-sm">
                <span className="tw-text-[rgb(107,114,128)]">
                  {formatFileSize(file?.size || selectedDocument?.size || 0)}
                </span>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <span className="tw-text-[#146C94]">{uploadProgress}%</span>
                )}
              </div>
              {uploadProgress > 0 && (
                <div className="tw-w-full tw-h-2 tw-bg-[#F3F4F6] tw-rounded-full tw-mt-2">
                  <div
                    className="tw-h-full tw-bg-[#146C94] tw-rounded-full tw-transition-all tw-duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}
            </div>
          )}

          {error && (
            <Typography className="tw-text-red-500 tw-text-sm">
              {error}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default FileUpload;
